import type {
	CourseAccreditation,
	CourseAccreditationByProfession,
	CourseCategory,
	CourseCategoryShallow,
} from "@brookbush/types";

import { NextSeo } from "next-seo";

import CoursesAccreditationSelect from "./CoursesAccreditationSelect";
import CoursesCard from "./CoursesCard";
import CardGrid from "~/components/CardGrid";
import Carousel from "~/components/Carousel";
import LoadingAnimation from "~/components/LoadingAnimation";
import { useCourseCategory } from "~/hooks";

const CoursesCategory = ({
	selectedAccreditation,
	accreditationsByProfession,
}: {
	shallowCategories: CourseCategoryShallow[];
	selectedAccreditation?: CourseAccreditation;
	accreditationsByProfession?: CourseAccreditationByProfession[];
}) => {
	const { data: category } = useCourseCategory();

	if (!category)
		return (
			<div className="flex h-[75vh] items-center justify-center">
				<LoadingAnimation />
			</div>
		);

	return (
		<>
			<NextSeo {...category} />

			{accreditationsByProfession && (
				<div className="my-1 phablet:hidden">
					<CoursesAccreditationSelect
						accreditationsByProfession={accreditationsByProfession}
					/>
				</div>
			)}

			<div className="mt-4 flex w-full flex-col gap-4 phablet:gap-6">
				<h1 className="phablet:text-left text-center font-semibold laptop:text-3xl phablet:text-3xl text-2xl xl:-mt-1">
					{category.title}
				</h1>

				{selectedAccreditation && (
					<p className="-mt-0.5 font-medium text-sky-700 dark:text-sky-200">
						Filtered Results For {selectedAccreditation.title}
					</p>
				)}

				{(category as CourseCategory).subCategories?.length ? (
					<div className="flex w-full flex-col gap-4">
						{(category as CourseCategory).subCategories!.map((subCategory) => (
							<Carousel
								hasMore
								title={subCategory.title}
								key={subCategory._id}
								href={`/courses/categories/${category.slug}/${
									subCategory.slug
								}${
									selectedAccreditation
										? "?accreditation=" + selectedAccreditation.slug
										: ""
								}`}
							>
								{subCategory.courses.map((course) => (
									<CoursesCard key={course._id} {...course} />
								))}
							</Carousel>
						))}
					</div>
				) : category.courses?.length ? (
					<CardGrid>
						{category.courses.map((course) => (
							<div className="card-grid-node-basis" key={course._id}>
								<CoursesCard {...course} />
							</div>
						))}
					</CardGrid>
				) : (
					<div className="flex h-[calc(100vh-20rem)] w-full items-center justify-center">
						<h2 className="font-semibold phablet:text-2xl text-xl">
							No Courses Found
						</h2>
					</div>
				)}
			</div>
		</>
	);
};

export default CoursesCategory;
